import React, { useState, useEffect, useCallback } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  ArrowDownIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  BoltIcon,
  ClipboardDocumentCheckIcon,
  UserPlusIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  FolderOpenIcon,
  PlayIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  Bars3Icon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { agentsAPI } from '../services/api';

// Workflow element types with their configuration
const WORKFLOW_ELEMENTS = {
  greeting: {
    type: 'greeting',
    label: 'Greeting',
    icon: ChatBubbleLeftRightIcon,
    color: 'bg-green-100 text-green-700 border-green-300',
    dragColor: 'bg-green-500',
    description: 'Welcome message to start conversation',
    defaultConfig: {
      messages: ['Hi there! 👋 How can I help you today?'],
      randomize: false
    }
  },
  question: {
    type: 'question',
    label: 'Question',
    icon: QuestionMarkCircleIcon,
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    dragColor: 'bg-blue-500',
    description: 'Ask user for information',
    defaultConfig: {
      question: '',
      field: '',
      required: true,
      validation: 'none',
      followUp: ''
    }
  },
  action: {
    type: 'action',
    label: 'Action',
    icon: BoltIcon,
    color: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    dragColor: 'bg-yellow-500',
    description: 'Perform an automated action',
    defaultConfig: {
      actionType: 'log',
      parameters: {}
    }
  },
  condition: {
    type: 'condition',
    label: 'Condition',
    icon: ArrowPathIcon,
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    dragColor: 'bg-purple-500',
    description: 'Branch based on conditions',
    defaultConfig: {
      field: '',
      operator: 'equals',
      value: '',
      trueBranch: [],
      falseBranch: []
    }
  },
  dataCapture: {
    type: 'dataCapture',
    label: 'Data Capture',
    icon: ClipboardDocumentCheckIcon,
    color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    dragColor: 'bg-indigo-500',
    description: 'Capture and validate data fields',
    defaultConfig: {
      fields: [
        { name: 'name', label: 'Full Name', type: 'text', required: true },
        { name: 'email', label: 'Email', type: 'email', required: true }
      ]
    }
  },
  leadCapture: {
    type: 'leadCapture',
    label: 'Lead Capture',
    icon: UserPlusIcon,
    color: 'bg-pink-100 text-pink-700 border-pink-300',
    dragColor: 'bg-pink-500',
    description: 'Capture lead information',
    defaultConfig: {
      captureFields: ['name', 'email', 'phone', 'company'],
      createInCRM: true,
      notifySales: true
    }
  },
  schedule: {
    type: 'schedule',
    label: 'Schedule Meeting',
    icon: CalendarDaysIcon,
    color: 'bg-orange-100 text-orange-700 border-orange-300',
    dragColor: 'bg-orange-500',
    description: 'Schedule a meeting or demo',
    defaultConfig: {
      meetingType: 'demo',
      duration: 30,
      calendarIntegration: true
    }
  },
  sendEmail: {
    type: 'sendEmail',
    label: 'Send Email',
    icon: EnvelopeIcon,
    color: 'bg-cyan-100 text-cyan-700 border-cyan-300',
    dragColor: 'bg-cyan-500',
    description: 'Send an automated email',
    defaultConfig: {
      template: '',
      to: '{{lead.email}}',
      subject: ''
    }
  },
  handoff: {
    type: 'handoff',
    label: 'Human Handoff',
    icon: PhoneIcon,
    color: 'bg-red-100 text-red-700 border-red-300',
    dragColor: 'bg-red-500',
    description: 'Transfer to human agent',
    defaultConfig: {
      department: 'sales',
      priority: 'normal',
      message: 'Transferring you to a specialist...'
    }
  },
  endConversation: {
    type: 'endConversation',
    label: 'End Conversation',
    icon: CheckCircleIcon,
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    dragColor: 'bg-gray-500',
    description: 'End the conversation flow',
    defaultConfig: {
      message: 'Thank you for your time! Have a great day!',
      satisfaction_survey: false
    }
  }
};

// Element palette component
const ElementPalette = ({ onDragStart }) => {
  const [expandedCategory, setExpandedCategory] = useState('all');

  const categories = {
    conversation: {
      label: 'Conversation',
      elements: ['greeting', 'question', 'endConversation']
    },
    data: {
      label: 'Data & Capture',
      elements: ['dataCapture', 'leadCapture']
    },
    actions: {
      label: 'Actions',
      elements: ['action', 'sendEmail', 'schedule']
    },
    flow: {
      label: 'Flow Control',
      elements: ['condition', 'handoff']
    }
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 flex items-center">
          <Bars3Icon className="w-5 h-5 mr-2" />
          Workflow Elements
        </h3>
        <p className="text-xs text-gray-500 mt-1">Drag elements to the canvas</p>
      </div>

      <div className="p-2">
        {Object.entries(categories).map(([categoryId, category]) => (
          <div key={categoryId} className="mb-2">
            <button
              onClick={() => setExpandedCategory(expandedCategory === categoryId ? 'all' : categoryId)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
            >
              <span>{category.label}</span>
              {expandedCategory === categoryId ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
            
            {(expandedCategory === 'all' || expandedCategory === categoryId) && (
              <div className="mt-1 space-y-1 pl-2">
                {category.elements.map((elementType) => {
                  const element = WORKFLOW_ELEMENTS[elementType];
                  const Icon = element.icon;
                  return (
                    <div
                      key={elementType}
                      draggable
                      onDragStart={(e) => onDragStart(e, elementType)}
                      className={`flex items-center p-2 rounded-md cursor-grab active:cursor-grabbing border ${element.color} hover:shadow-md transition-shadow`}
                    >
                      <Icon className="w-5 h-5 mr-2 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{element.label}</div>
                        <div className="text-xs opacity-75 truncate">{element.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Workflow canvas node component
const WorkflowNode = ({ node, index, isSelected, onSelect, onDelete, onUpdate, totalNodes }) => {
  const element = WORKFLOW_ELEMENTS[node.type];
  const Icon = element?.icon || BoltIcon;

  return (
    <div className="flex flex-col items-center">
      <div
        onClick={() => onSelect(node.id)}
        className={`relative w-full max-w-md p-4 rounded-lg border-2 cursor-pointer transition-all ${
          isSelected 
            ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' 
            : `border-gray-200 hover:border-gray-300 ${element?.color || 'bg-gray-100'}`
        }`}
      >
        {/* Node header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <div className={`p-1.5 rounded-md ${element?.dragColor || 'bg-gray-500'} text-white mr-2`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className="font-medium text-gray-900">{element?.label || node.type}</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-xs text-gray-400">#{index + 1}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(node.id);
              }}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Node content preview */}
        <div className="text-sm text-gray-600">
          {node.type === 'greeting' && (
            <p className="truncate italic">"{node.config?.messages?.[0] || 'Configure greeting...'}"</p>
          )}
          {node.type === 'question' && (
            <p className="truncate">{node.config?.question || 'Set question text...'}</p>
          )}
          {node.type === 'dataCapture' && (
            <p>{node.config?.fields?.length || 0} fields to capture</p>
          )}
          {node.type === 'leadCapture' && (
            <p>Capture: {node.config?.captureFields?.join(', ') || 'Configure fields...'}</p>
          )}
          {node.type === 'action' && (
            <p>Action: {node.config?.actionType || 'Configure action...'}</p>
          )}
          {node.type === 'condition' && (
            <p>If {node.config?.field || '...'} {node.config?.operator || '...'} {node.config?.value || '...'}</p>
          )}
          {node.type === 'schedule' && (
            <p>Schedule {node.config?.meetingType || 'meeting'} ({node.config?.duration || 30} min)</p>
          )}
          {node.type === 'sendEmail' && (
            <p>Send to: {node.config?.to || 'Configure recipient...'}</p>
          )}
          {node.type === 'handoff' && (
            <p>Transfer to {node.config?.department || 'agent'}</p>
          )}
          {node.type === 'endConversation' && (
            <p className="truncate italic">"{node.config?.message || 'Set closing message...'}"</p>
          )}
        </div>
      </div>

      {/* Connector arrow */}
      {index < totalNodes - 1 && (
        <div className="flex flex-col items-center py-2">
          <div className="w-0.5 h-4 bg-gray-300"></div>
          <ArrowDownIcon className="w-4 h-4 text-gray-400" />
          <div className="w-0.5 h-4 bg-gray-300"></div>
        </div>
      )}
    </div>
  );
};

// Property editor component
const PropertyEditor = ({ node, onUpdate, onClose }) => {
  const [config, setConfig] = useState(node?.config || {});
  const element = WORKFLOW_ELEMENTS[node?.type];

  useEffect(() => {
    setConfig(node?.config || {});
  }, [node]);

  const handleChange = (field, value) => {
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
    onUpdate(node.id, { ...node, config: newConfig });
  };

  const handleArrayChange = (field, index, value) => {
    const arr = [...(config[field] || [])];
    arr[index] = value;
    handleChange(field, arr);
  };

  const addArrayItem = (field, defaultValue) => {
    const arr = [...(config[field] || []), defaultValue];
    handleChange(field, arr);
  };

  const removeArrayItem = (field, index) => {
    const arr = [...(config[field] || [])];
    arr.splice(index, 1);
    handleChange(field, arr);
  };

  if (!node) {
    return (
      <div className="w-80 bg-white border-l border-gray-200 p-4">
        <div className="text-center text-gray-500 py-8">
          <Cog6ToothIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Select an element to configure</p>
        </div>
      </div>
    );
  }

  const Icon = element?.icon || BoltIcon;

  return (
    <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center">
          <div className={`p-2 rounded-md ${element?.dragColor || 'bg-gray-500'} text-white mr-3`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{element?.label}</h3>
            <p className="text-xs text-gray-500">{element?.description}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Greeting config */}
        {node.type === 'greeting' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Greeting Messages
              </label>
              {(config.messages || ['']).map((msg, idx) => (
                <div key={idx} className="flex items-start mb-2">
                  <textarea
                    value={msg}
                    onChange={(e) => handleArrayChange('messages', idx, e.target.value)}
                    rows={2}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Enter greeting message..."
                  />
                  <button
                    onClick={() => removeArrayItem('messages', idx)}
                    className="ml-2 p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addArrayItem('messages', '')}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                + Add message variation
              </button>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.randomize || false}
                onChange={(e) => handleChange('randomize', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Randomize messages</label>
            </div>
          </>
        )}

        {/* Question config */}
        {node.type === 'question' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
              <textarea
                value={config.question || ''}
                onChange={(e) => handleChange('question', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="What would you like to ask?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Save to Field</label>
              <input
                type="text"
                value={config.field || ''}
                onChange={(e) => handleChange('field', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="e.g., user_name, budget"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Validation</label>
              <select
                value={config.validation || 'none'}
                onChange={(e) => handleChange('validation', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="none">None</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.required !== false}
                onChange={(e) => handleChange('required', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Required field</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Prompt</label>
              <input
                type="text"
                value={config.followUp || ''}
                onChange={(e) => handleChange('followUp', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Optional follow-up if invalid"
              />
            </div>
          </>
        )}

        {/* Data Capture config */}
        {node.type === 'dataCapture' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fields to Capture</label>
              {(config.fields || []).map((field, idx) => (
                <div key={idx} className="p-3 border border-gray-200 rounded-md mb-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Field {idx + 1}</span>
                    <button
                      onClick={() => removeArrayItem('fields', idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={field.name || ''}
                    onChange={(e) => handleArrayChange('fields', idx, { ...field, name: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-1"
                    placeholder="Field name (e.g., email)"
                  />
                  <input
                    type="text"
                    value={field.label || ''}
                    onChange={(e) => handleArrayChange('fields', idx, { ...field, label: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-1"
                    placeholder="Display label"
                  />
                  <select
                    value={field.type || 'text'}
                    onChange={(e) => handleArrayChange('fields', idx, { ...field, type: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="number">Number</option>
                    <option value="select">Select</option>
                  </select>
                </div>
              ))}
              <button
                onClick={() => addArrayItem('fields', { name: '', label: '', type: 'text', required: true })}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                + Add field
              </button>
            </div>
          </>
        )}

        {/* Lead Capture config */}
        {node.type === 'leadCapture' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Capture Fields</label>
              <div className="space-y-2">
                {['name', 'email', 'phone', 'company', 'role', 'website'].map((field) => (
                  <label key={field} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={(config.captureFields || []).includes(field)}
                      onChange={(e) => {
                        const fields = config.captureFields || [];
                        if (e.target.checked) {
                          handleChange('captureFields', [...fields, field]);
                        } else {
                          handleChange('captureFields', fields.filter(f => f !== field));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">{field}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.createInCRM !== false}
                onChange={(e) => handleChange('createInCRM', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Create lead in CRM</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.notifySales !== false}
                onChange={(e) => handleChange('notifySales', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Notify sales team</label>
            </div>
          </>
        )}

        {/* Action config */}
        {node.type === 'action' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
              <select
                value={config.actionType || 'log'}
                onChange={(e) => handleChange('actionType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="log">Log Event</option>
                <option value="webhook">Call Webhook</option>
                <option value="updateCRM">Update CRM</option>
                <option value="sendNotification">Send Notification</option>
                <option value="setVariable">Set Variable</option>
              </select>
            </div>
          </>
        )}

        {/* Schedule config */}
        {node.type === 'schedule' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
              <select
                value={config.meetingType || 'demo'}
                onChange={(e) => handleChange('meetingType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="demo">Product Demo</option>
                <option value="call">Discovery Call</option>
                <option value="consultation">Consultation</option>
                <option value="followup">Follow-up Meeting</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
              <select
                value={config.duration || 30}
                onChange={(e) => handleChange('duration', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.calendarIntegration !== false}
                onChange={(e) => handleChange('calendarIntegration', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Use calendar integration</label>
            </div>
          </>
        )}

        {/* Send Email config */}
        {node.type === 'sendEmail' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To (recipient)</label>
              <input
                type="text"
                value={config.to || ''}
                onChange={(e) => handleChange('to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="{{lead.email}}"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={config.subject || ''}
                onChange={(e) => handleChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Email subject..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
              <select
                value={config.template || ''}
                onChange={(e) => handleChange('template', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">Select template...</option>
                <option value="welcome">Welcome Email</option>
                <option value="followup">Follow-up Email</option>
                <option value="confirmation">Confirmation Email</option>
              </select>
            </div>
          </>
        )}

        {/* Handoff config */}
        {node.type === 'handoff' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={config.department || 'sales'}
                onChange={(e) => handleChange('department', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="sales">Sales</option>
                <option value="support">Support</option>
                <option value="technical">Technical</option>
                <option value="billing">Billing</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={config.priority || 'normal'}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Handoff Message</label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Message shown during transfer..."
              />
            </div>
          </>
        )}

        {/* End Conversation config */}
        {node.type === 'endConversation' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Closing Message</label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Thank you message..."
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.satisfaction_survey || false}
                onChange={(e) => handleChange('satisfaction_survey', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">Show satisfaction survey</label>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main Workflow Studio component
const WorkflowStudioPage = () => {
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [nodes, setNodes] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);

  // Load existing workflows
  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoadingWorkflows(true);
      const response = await agentsAPI.getWorkflows();
      setWorkflows(response.data?.workflows || []);
    } catch (error) {
      console.error('Error loading workflows:', error);
    } finally {
      setLoadingWorkflows(false);
    }
  };

  const handleDragStart = (e, elementType) => {
    e.dataTransfer.setData('elementType', elementType);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const elementType = e.dataTransfer.getData('elementType');
    if (elementType && WORKFLOW_ELEMENTS[elementType]) {
      const newNode = {
        id: `node_${Date.now()}`,
        type: elementType,
        config: { ...WORKFLOW_ELEMENTS[elementType].defaultConfig }
      };
      setNodes([...nodes, newNode]);
      setSelectedNodeId(newNode.id);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDeleteNode = (nodeId) => {
    setNodes(nodes.filter(n => n.id !== nodeId));
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
    }
  };

  const handleUpdateNode = (nodeId, updatedNode) => {
    setNodes(nodes.map(n => n.id === nodeId ? updatedNode : n));
  };

  const selectedNode = nodes.find(n => n.id === selectedNodeId);

  // Generate workflow JSON
  const generateWorkflowJSON = () => {
    const workflowId = workflowName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    // Convert nodes to stages structure
    const stages = {};
    nodes.forEach((node, index) => {
      const stageId = `stage_${index + 1}`;
      stages[stageId] = {
        order: index + 1,
        name: WORKFLOW_ELEMENTS[node.type]?.label || node.type,
        description: WORKFLOW_ELEMENTS[node.type]?.description || '',
        type: node.type,
        config: node.config,
        entry_conditions: index === 0 ? [] : [`stage_${index}_completed`],
        exit_conditions: [`${stageId}_completed`],
        data_to_capture: extractDataFields(node)
      };
    });

    return {
      workflow_id: workflowId,
      name: workflowName,
      description: workflowDescription,
      version: '1.0',
      active: true,
      stages,
      data_schema: generateDataSchema(nodes),
      integrations: extractIntegrations(nodes),
      metadata: {
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'workflow_studio',
        version: '1.0'
      }
    };
  };

  const extractDataFields = (node) => {
    const fields = [];
    if (node.type === 'question' && node.config?.field) {
      fields.push(node.config.field);
    }
    if (node.type === 'dataCapture' && node.config?.fields) {
      fields.push(...node.config.fields.map(f => f.name));
    }
    if (node.type === 'leadCapture' && node.config?.captureFields) {
      fields.push(...node.config.captureFields);
    }
    return fields;
  };

  const generateDataSchema = (nodes) => {
    const lead = {};
    nodes.forEach(node => {
      if (node.type === 'question' && node.config?.field) {
        lead[node.config.field] = { 
          type: node.config.validation || 'string', 
          required: node.config.required !== false 
        };
      }
      if (node.type === 'dataCapture' && node.config?.fields) {
        node.config.fields.forEach(f => {
          lead[f.name] = { type: f.type || 'string', required: f.required !== false };
        });
      }
      if (node.type === 'leadCapture' && node.config?.captureFields) {
        node.config.captureFields.forEach(f => {
          lead[f] = { type: f === 'email' ? 'email' : f === 'phone' ? 'phone' : 'string', required: true };
        });
      }
    });
    return { lead };
  };

  const extractIntegrations = (nodes) => {
    const integrations = {};
    nodes.forEach(node => {
      if (node.type === 'leadCapture' && node.config?.createInCRM) {
        integrations.crm = { create_lead: true };
      }
      if (node.type === 'sendEmail') {
        integrations.email = { enabled: true };
      }
      if (node.type === 'schedule' && node.config?.calendarIntegration) {
        integrations.calendar = { enabled: true };
      }
    });
    return integrations;
  };

  const handleSaveWorkflow = async () => {
    if (!workflowName.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }
    if (nodes.length === 0) {
      toast.error('Please add at least one element to the workflow');
      return;
    }

    try {
      setIsSaving(true);
      const workflowData = generateWorkflowJSON();
      
      const response = await fetch('http://localhost:8000/api/v1/agents/workflows', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(workflowData)
      });

      if (response.ok) {
        toast.success('Workflow saved successfully!');
        loadWorkflows();
      } else {
        throw new Error('Failed to save workflow');
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error('Failed to save workflow');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadWorkflow = async (workflowId) => {
    try {
      const response = await agentsAPI.getWorkflow(workflowId);
      const workflow = response.data;
      
      setWorkflowName(workflow.name || 'Loaded Workflow');
      setWorkflowDescription(workflow.description || '');
      
      // Convert stages back to nodes
      if (workflow.stages) {
        const loadedNodes = Object.entries(workflow.stages)
          .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
          .map(([stageId, stage]) => ({
            id: stageId,
            type: stage.type || 'action',
            config: stage.config || {}
          }));
        setNodes(loadedNodes);
      }
      
      setShowLoadModal(false);
      toast.success('Workflow loaded successfully!');
    } catch (error) {
      console.error('Error loading workflow:', error);
      toast.error('Failed to load workflow');
    }
  };

  const handleNewWorkflow = () => {
    setWorkflowName('New Workflow');
    setWorkflowDescription('');
    setNodes([]);
    setSelectedNodeId(null);
  };

  const handleExportJSON = () => {
    const workflowData = generateWorkflowJSON();
    const blob = new Blob([JSON.stringify(workflowData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflowData.workflow_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Workflow exported!');
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <ArrowPathIcon className="w-8 h-8 text-purple-600" />
            <div>
              <input
                type="text"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                className="text-2xl font-bold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-purple-500 rounded px-1 -ml-1"
                placeholder="Workflow Name"
              />
              <input
                type="text"
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                className="block text-sm text-gray-500 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-purple-500 rounded px-1 -ml-1 w-96"
                placeholder="Add a description..."
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleNewWorkflow}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <PlusIcon className="w-5 h-5 inline mr-1" />
              New
            </button>
            <button
              onClick={() => setShowLoadModal(true)}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <FolderOpenIcon className="w-5 h-5 inline mr-1" />
              Load
            </button>
            <button
              onClick={handleExportJSON}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <DocumentArrowDownIcon className="w-5 h-5 inline mr-1" />
              Export
            </button>
            <button
              onClick={handleSaveWorkflow}
              disabled={isSaving}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Workflow'}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Element palette */}
        <ElementPalette onDragStart={handleDragStart} />

        {/* Center - Canvas */}
        <div 
          className="flex-1 overflow-y-auto p-8"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {nodes.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500 border-2 border-dashed border-gray-300 rounded-lg p-12 max-w-md">
                <ArrowPathIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Start Building Your Workflow</h3>
                <p className="text-sm">Drag elements from the left panel and drop them here to create your conversation flow.</p>
              </div>
            </div>
          ) : (
            <div className="max-w-lg mx-auto space-y-0">
              {nodes.map((node, index) => (
                <WorkflowNode
                  key={node.id}
                  node={node}
                  index={index}
                  isSelected={selectedNodeId === node.id}
                  onSelect={setSelectedNodeId}
                  onDelete={handleDeleteNode}
                  onUpdate={handleUpdateNode}
                  totalNodes={nodes.length}
                />
              ))}
              
              {/* Add element hint */}
              <div className="flex justify-center pt-4">
                <div className="text-center text-gray-400 text-sm">
                  <p>Drag more elements here to extend the flow</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar - Property editor */}
        <PropertyEditor
          node={selectedNode}
          onUpdate={handleUpdateNode}
          onClose={() => setSelectedNodeId(null)}
        />
      </div>

      {/* Load Modal */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 sm:mx-auto p-6">
            <h3 className="text-lg font-semibold mb-4">Load Workflow</h3>
            {loadingWorkflows ? (
              <div className="text-center py-8">Loading...</div>
            ) : workflows.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No saved workflows found.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {workflows.map((workflow) => (
                  <button
                    key={workflow.id}
                    onClick={() => handleLoadWorkflow(workflow.id)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{workflow.name}</div>
                    <div className="text-sm text-gray-500">{workflow.stages_count} stages</div>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowLoadModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowStudioPage;
